/**
 * Проверяет, является ли устройство мобильным
 */
export const isMobileDevice = () => {
    if (typeof window === "undefined") {
        return false;
    }
    // Проверяем user agent
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileByUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    // Дополнительная проверка: должно быть touch устройство
    const hasTouchSupport = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    // Проверяем размер экрана (мобильные обычно меньше 1024px)
    const isSmallScreen = window.innerWidth <= 1024 && window.screen.width <= 1024;
    return isMobileByUserAgent && hasTouchSupport && isSmallScreen;
};
/**
 * Проверяет, запущено ли приложение в Telegram Mini App.
 *
 * Признак — НЕПУСТОЙ `window.Telegram.WebApp.initData` (по аналогии с
 * isMaxMiniApp в max.ts): подписанную строку хост Telegram кладёт в мост
 * только внутри клиента. Скрипт telegram-web-app.js подключён глобально
 * с `defer` в public/index.html, поэтому `window.Telegram.WebApp` есть и на
 * обычном вебе — но там initData пустой, так что ложных срабатываний нет.
 *
 * Раньше детект шёл по самому факту наличия `window.Telegram.WebApp` и по
 * хрупким сигналам (UA / hash / referrer). Без глобального скрипта на холодном
 * старте мобильного клиента моста ещё не было → детект срывался и авторизация
 * уходила в гостя (баг «на телефоне не подтягивается аккаунт»). А после
 * подключения глобального скрипта presence-проверка стала бы ловить обычный
 * веб как мини-апп. UA/referrer тоже давали ложные срабатывания (например,
 * внешний браузер, открытый по ссылке t.me). Поэтому единственный надёжный
 * признак — непустой initData; глобальный скрипт делает его доступным
 * синхронно до старта React на всех клиентах (десктоп и мобайл).
 */
export const isTelegramMiniApp = () => {
    if (typeof window === "undefined") {
        return false;
    }
    // Тестовый флаг. localStorage на iOS/WebView бывает null — без try роняем рендер.
    try {
        if (localStorage?.getItem("isTelegramMiniApp") === "true") {
            return true;
        }
    }
    catch {
        /* storage unavailable */
    }
    // Подписанный initData есть только внутри клиента Telegram.
    const initData = window.Telegram?.WebApp?.initData;
    return typeof initData === "string" && initData.trim().length > 0;
};
/**
 * Загружает скрипт Telegram WebApp, если его нет
 */
export const loadTelegramWebAppScript = () => {
    return new Promise((resolve, reject) => {
        if (typeof window === "undefined") {
            reject(new Error("Window is not available"));
            return;
        }
        // Если скрипт уже загружен
        if (window.Telegram?.WebApp) {
            resolve();
            return;
        }
        // Если скрипт уже загружается
        const existingScript = document.querySelector('script[src*="telegram.org"]');
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve());
            existingScript.addEventListener("error", () => reject(new Error("Failed to load Telegram WebApp script")));
            return;
        }
        // Загружаем скрипт
        const script = document.createElement("script");
        script.src = "https://telegram.org/js/telegram-web-app.js";
        script.async = true;
        script.onload = () => {
            // Ждем, пока WebApp инициализируется
            const checkInterval = setInterval(() => {
                if (window.Telegram?.WebApp) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 50);
            // Таймаут на случай, если скрипт загрузился, но WebApp не инициализировался
            setTimeout(() => {
                clearInterval(checkInterval);
                if (window.Telegram?.WebApp) {
                    resolve();
                }
                else {
                    reject(new Error("Telegram WebApp script loaded but WebApp is not available"));
                }
            }, 2000);
        };
        script.onerror = () => reject(new Error("Failed to load Telegram WebApp script"));
        document.head.appendChild(script);
    });
};
/**
 * Получает объект Telegram WebApp API (если доступен)
 */
export const getTelegramWebApp = () => {
    if (typeof window !== "undefined" && window.Telegram?.WebApp) {
        return window.Telegram.WebApp;
    }
    return null;
};
/**
 * Получает initData для тестирования из URL параметров или localStorage
 * Для тестирования можно передать initData через URL параметр ?initData=...
 * или установить localStorage.setItem("testInitData", "...")
 */
export const getTestInitData = () => {
    if (typeof window === "undefined") {
        return null;
    }
    // Проверяем URL параметры
    const searchParams = new URLSearchParams(window.location.search);
    const urlInitData = searchParams.get("initData");
    if (urlInitData) {
        return urlInitData;
    }
    try {
        return localStorage?.getItem("testInitData") ?? null;
    }
    catch {
        return null;
    }
};
/**
 * Получает initData из Telegram WebApp или тестовых источников
 */
export const getInitData = () => {
    const webApp = getTelegramWebApp();
    // Проверяем, что initData существует и не пустой
    if (webApp?.initData && webApp.initData.trim().length > 0) {
        return webApp.initData;
        // return "user=%7B%22id%22%3A+405222173%2C+%22first_name%22%3A+%22Test%22%2C+%22is_bot%22%3A+false%2C+%22is_premium%22%3A+false%2C+%22language_code%22%3A+%22ru%22%7D&auth_date=1768972740";
    }
    // Альтернативный способ: проверяем initDataUnsafe (может быть доступен раньше)
    if (webApp?.initDataUnsafe && Object.keys(webApp.initDataUnsafe).length > 0) {
        // Если initDataUnsafe доступен, но initData нет, это может означать,
        // что нужно подождать или использовать другой способ получения
        console.log("initDataUnsafe available but initData is not:", webApp.initDataUnsafe);
    }
    // Проверяем URL параметры (Telegram может передавать initData через URL)
    const searchParams = new URLSearchParams(window.location.search);
    const tgWebAppData = searchParams.get("tgWebAppData");
    if (tgWebAppData) {
        return tgWebAppData;
    }
    // Проверяем hash параметры
    const hash = window.location.hash;
    if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const hashInitData = hashParams.get("tgWebAppData");
        if (hashInitData) {
            return hashInitData;
        }
    }
    // Для тестирования в браузере
    const testData = getTestInitData();
    if (testData) {
        return testData;
    }
    return null;
};
/**
 * Ждет, пока Telegram WebApp инициализируется и initData станет доступен
 */
export const waitForInitData = async (maxWaitTime = 2000, checkInterval = 100) => {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTime) {
        const initData = getInitData();
        if (initData) {
            return initData;
        }
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }
    return null;
};
